import { Moment } from 'moment';
import * as yup from 'yup';
import type {
    Contest,
    ContestConsentOption,
    ContestGeneral,
    ContestSkillChallenge,
} from '../../../stores/contest';
import type { PromotionFormProvince } from '../../../stores/promotion';
import {
    convertLocalToUtcString,
    convertUtcToLocalMoment,
    DEFAULT_TIMEZONE,
} from '../../../utils/timezone';

export const LANGUAGES = ['en', 'fr'] as const;
export type Language = typeof LANGUAGES[number];

/**
 * A consent checkbox as edited. With `sameForCircleK`, Circle K shows the
 * Couche-Tard wording and its own fields are hidden.
 */
export type ConsentOptionForm = ContestConsentOption & {
    sameForCircleK: boolean;
};

/** The dates are shown and typed in the contest's time zone. */
export interface GeneralFormValues {
    translations: Record<Language, { title: string; urlSegment: string }>;
    startDate: Moment | null;
    endDate: Moment | null;
    drawAt: Moment | null;
    timeZone: string;
    provinces: PromotionFormProvince[];
    minAge: number | null;
    maxEntriesPerDay: number | null;
    maxEntriesTotal: number | null;
    routeEntryQuantity: number;
    routeMaxEntriesPerDay: number | null;
    routeMaxEntriesTotal: number | null;
    retentionDays: number;
    noPurchaseCode: string;
    skillChallenge: ContestSkillChallenge;
    consentOptions: ConsentOptionForm[];
}

/** Most sponsors ask the winner themselves, outside the contest. */
const DEFAULT_SKILL_CHALLENGE: ContestSkillChallenge = 'winner';

export const NEW_CONTEST_VALUES: GeneralFormValues = {
    translations: {
        en: { title: '', urlSegment: '' },
        fr: { title: '', urlSegment: '' },
    },
    startDate: null,
    endDate: null,
    drawAt: null,
    timeZone: DEFAULT_TIMEZONE,
    provinces: [],
    minAge: null,
    maxEntriesPerDay: null,
    maxEntriesTotal: null,
    routeEntryQuantity: 1,
    routeMaxEntriesPerDay: null,
    routeMaxEntriesTotal: null,
    retentionDays: 365,
    noPurchaseCode: '',
    skillChallenge: DEFAULT_SKILL_CHALLENGE,
    consentOptions: [],
};

export function toFormValues(contest: Contest): GeneralFormValues {
    const local = (utc: string | null) =>
        utc ? convertUtcToLocalMoment(utc, contest.timeZone) : null;
    const translation = (lang: Language) => {
        const found = contest.translations.find((t) => t.lang === lang);
        return {
            title: found?.title ?? '',
            urlSegment: found?.urlSegment ?? '',
        };
    };
    return {
        translations: { en: translation('en'), fr: translation('fr') },
        startDate: local(contest.startAt),
        endDate: local(contest.endAt),
        drawAt: local(contest.drawAt),
        timeZone: contest.timeZone,
        provinces: contest.provinceIds.map((provinceId) => ({
            provinceId,
            businessUnitId: null,
        })),
        minAge: contest.minAge,
        maxEntriesPerDay: contest.maxEntriesPerDay,
        maxEntriesTotal: contest.maxEntriesTotal,
        routeEntryQuantity: contest.routeEntryQuantity,
        routeMaxEntriesPerDay: contest.routeMaxEntriesPerDay,
        routeMaxEntriesTotal: contest.routeMaxEntriesTotal,
        retentionDays: contest.retentionDays,
        noPurchaseCode: contest.noPurchaseCode ?? '',
        skillChallenge: contest.skillChallenge,
        consentOptions: contest.consentOptions.map((option) => ({
            ...option,
            sameForCircleK: option.labels.every((label) => !label.labelCa),
        })),
    };
}

/** Called once the form is valid, so every required date is set. */
export function toGeneral(values: GeneralFormValues): ContestGeneral {
    // A date still in the contest's time zone keeps its instant. Reading its
    // wall clock back would pick the first of the two hours a fall back
    // repeats. A date picked or a zone changed is read in the contest's zone.
    const utc = (local: Moment | null) => {
        if (!local) {
            return null;
        }
        return local.tz() === values.timeZone
            ? local.toISOString()
            : convertLocalToUtcString(local, values.timeZone);
    };
    return {
        translations: LANGUAGES.map((lang) => ({
            lang,
            title: values.translations[lang].title.trim(),
            urlSegment: values.translations[lang].urlSegment.trim(),
        })),
        startAt: utc(values.startDate) as string,
        endAt: utc(values.endDate) as string,
        // A day: participants are shown the date alone.
        drawAt: utc(values.drawAt?.clone().startOf('day') ?? null),
        timeZone: values.timeZone,
        provinceIds: values.provinces.map((province) => province.provinceId),
        minAge: values.minAge,
        maxEntriesPerDay: values.maxEntriesPerDay,
        maxEntriesTotal: values.maxEntriesTotal,
        routeEntryQuantity: values.routeEntryQuantity,
        routeMaxEntriesPerDay: values.routeMaxEntriesPerDay,
        routeMaxEntriesTotal: values.routeMaxEntriesTotal,
        retentionDays: values.retentionDays,
        noPurchaseCode: values.noPurchaseCode.trim() || null,
        skillChallenge: values.skillChallenge,
        consentOptions: values.consentOptions.map(
            ({ sameForCircleK, ...option }) => ({
                ...option,
                labels: option.labels.map((label) => ({
                    ...label,
                    labelQc: label.labelQc.trim(),
                    labelCa: sameForCircleK
                        ? null
                        : label.labelCa?.trim() || null,
                })),
            })
        ),
    };
}

const optionalCount = yup
    .number()
    .integer()
    .positive()
    .nullable()
    .default(null);

const URL_SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The API refuses less, so the anonymization never runs before the draw. */
export const MIN_RETENTION_DAYS = 365;

/**
 * An address proposed from a title: lowercase, without accents, words joined
 * by hyphens.
 */
export function proposeUrlSegment(title: string): string {
    return title
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 100)
        .replace(/-+$/, '');
}

const translationSchema = yup.object({
    title: yup.string().trim().max(150).required('A title is required'),
    urlSegment: yup
        .string()
        .trim()
        .max(100)
        .matches(URL_SEGMENT, {
            message: 'Lowercase letters and digits, joined by hyphens',
        })
        .required('An address is required'),
});

/** The API applies the same rules; these show them before a round trip. */
export function generalFormSchema() {
    return yup.object({
        translations: yup.object({
            en: translationSchema,
            fr: translationSchema,
        }),
        startDate: yup.mixed<Moment>().nullable().required(),
        endDate: yup
            .mixed<Moment>()
            .nullable()
            .required()
            .test('after-start', 'The end comes after the start', function (
                end
            ) {
                const start = this.parent.startDate as Moment | null;
                return !start || !end || end.isAfter(start);
            }),
        timeZone: yup.string().required(),
        minAge: optionalCount,
        maxEntriesPerDay: optionalCount,
        maxEntriesTotal: optionalCount,
        routeEntryQuantity: yup.number().integer().positive().required(),
        routeMaxEntriesPerDay: optionalCount,
        routeMaxEntriesTotal: optionalCount,
        retentionDays: yup
            .number()
            .integer()
            .min(
                MIN_RETENTION_DAYS,
                `At least ${MIN_RETENTION_DAYS} days, so the information outlives the draw`
            )
            .required(),
        noPurchaseCode: yup.string().trim().max(50),
        consentOptions: yup
            .array(
                yup.object({
                    labels: yup.array(
                        yup.object({
                            labelQc: yup.string().trim().max(500).required(),
                            labelCa: yup.string().trim().max(500).nullable(),
                        })
                    ),
                })
            )
            .test(
                'unique',
                'Each recipient and channel is offered once',
                (options) =>
                    new Set(
                        (options ?? []).map(
                            (option) =>
                                `${
                                    (option as ContestConsentOption).recipient
                                }:${(option as ContestConsentOption).channel}`
                        )
                    ).size === (options ?? []).length
            ),
    });
}
